import { INDEX_STATES, isPrivateOrLocalHostname } from '@repo/shared-types';
import { z } from 'zod';

/** Mirrors `@repo/shared-types`'s `IndexState` (index-verification design, 2026-08-14) — the
 * Zod counterpart every other DB-backed enum here has (`botSettingsPositionSchema`,
 * `leadCaptureModeSchema`, …), kept even though the Source Detail documents DTO isn't
 * currently response-validated: `GET /api/bots/:botId/sources/:sid/documents`'s row shape
 * (`SourceDocumentDto`) gains `indexState`/`indexError` in this same cycle, and this is
 * where a future filter/query param on that value (or a job payload validating it) reaches
 * for the shared Zod definition rather than hand-writing `z.enum([...])` again. */
export const indexStateSchema = z.enum(INDEX_STATES);

/** An http(s)-only URL string — plain z.string().url() would also accept ftp:// etc.
 *
 * The second refine below is layer 1 of the two-layer SSRF guard (see
 * `@repo/shared-types`'s `net.ts` doc comment) — it rejects an obviously private/loopback/
 * link-local/local-only URI at input time, for a fast, friendly error. **It is not the security
 * boundary**: a hostname that looks public here is free to resolve to a private address by the
 * time the crawler actually fetches it (or later, if its DNS changes after the source is
 * created). The authoritative check is `apps/api/src/crawler/net-guard.ts`, which resolves the
 * host and re-checks every redirect hop at fetch time.
 *
 * Exported (2026-08-12, public-playground Task 4) so `demo.ts`'s `createDemoSchema` reuses
 * this EXACT check for `POST /v1/demo`'s `url` field rather than hand-duplicating the two
 * refines — a demo is pre-auth (the design spec's whole reason Task 1's SSRF guard exists at
 * all), so it needs the fast, friendly rejection here at least as much as an authenticated
 * source does.
 */
export const httpUrl = z
  .string()
  .url()
  .refine(
    (value) => {
      try {
        return ['http:', 'https:'].includes(new URL(value).protocol);
      } catch {
        return false;
      }
    },
    { message: 'URL must use http or https protocol' },
  )
  .refine(
    (value) => {
      try {
        return !isPrivateOrLocalHostname(new URL(value).hostname);
      } catch {
        return true; // unreachable: the protocol refine above already parsed this successfully
      }
    },
    {
      message: 'URL must not point at a private, loopback, link-local, or local-only address',
    },
  );

export const createSourceSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('url'), uri: httpUrl }),
  z.object({ type: z.literal('sitemap'), uri: httpUrl }),
  z.object({
    type: z.literal('text'),
    uri: z.string().min(1).max(120), // title
    content: z.string().min(1).max(200_000),
  }),
]);

export type CreateSourceInput = z.infer<typeof createSourceSchema>;

/** `PATCH /api/bots/:botId/sources/:sid` — currently the only mutable field is
 * `renderedMode` (whether crawls of this source escalate to CF Browser Rendering). */
export const patchSourceSchema = z.object({ renderedMode: z.boolean() });

export type PatchSourceInput = z.infer<typeof patchSourceSchema>;

/** `GET /api/bots/:botId/sources/:sid/documents?page=` query (source-detail-diagnostics
 * spec) — page-NUMBER pagination, `perPage` deliberately NOT a query param: page size is
 * fixed server-side at `SOURCE_DOCUMENTS_PAGE_SIZE` (`@repo/db`), same "not caller-
 * configurable" convention as `adminPageQuerySchema`/`publicPostsQuerySchema`. */
export const sourceDocumentsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  /** THI-99 item 2 — `?nearEmpty=true` restricts the list to documents under
   * `NEAR_EMPTY_MIN_CHARS` (`@repo/shared-types`), same predicate the `nearEmpty` field on each
   * row is computed from. An explicit string-literal check, not `z.coerce.boolean()` — that
   * would treat the STRING `"false"` as truthy (any non-empty string coerces to `true`), so
   * anything other than the literal `"true"` — including a caller who mistakenly sends
   * `nearEmpty=false` — resolves to unfiltered. */
  nearEmpty: z
    .string()
    .optional()
    .transform((value) => value === 'true'),
});

export type SourceDocumentsQuery = z.infer<typeof sourceDocumentsQuerySchema>;
